import { useEffect, useState } from 'react'
import { getAblyClient, MAIN_CHANNEL } from '../data/ablyClient.js'
import { useUIStore } from '../store/uiStore.js'

/**
 * useGoalPresence — subscribes to which users are currently "editing" a
 * specific goal and, if `actAsEditor` is true, publishes this user's
 * presence.
 *
 * Uses Ably's built-in presence set, keyed by `goal:<pillarName>:<goalIndex>`.
 * Clients enter when they start editing, leave when they finish.
 *
 * Returns a list of other users currently editing (not including self).
 *
 * If Ably isn't configured, this hook quietly returns an empty list.
 */
export function useGoalPresence({ pillarName, goalIndex, actAsEditor = false }) {
  const currentUser = useUIStore((s) => s.currentUser)
  const [editors, setEditors] = useState([])

  useEffect(() => {
    let cancelled = false
    let channel = null
    let presenceKey = null
    let subscribedListener = null

    ;(async () => {
      const client = await getAblyClient()
      if (!client || cancelled) return

      channel = client.channels.get(MAIN_CHANNEL)
      presenceKey = `goal:${pillarName}:${goalIndex}`

      // Refresh the editors list from Ably's full presence set.
      // We store the user's display name in presence `data.name` so we can
      // render it regardless of what the Ably clientId happens to be.
      const refreshEditors = async () => {
        try {
          const members = await channel.presence.get()
          const activeForThisGoal = members.filter((m) => {
            if (m.data?.key !== presenceKey) return false
            // Exclude ourselves — match by either name or clientId
            const memberName = m.data?.name || m.clientId
            return memberName !== currentUser && m.clientId !== client.auth.clientId
          })
          if (!cancelled) {
            setEditors(
              activeForThisGoal.map((m) => ({
                name: m.data?.name || m.clientId || 'Unknown',
                at: m.timestamp || Date.now(),
              }))
            )
          }
        } catch (err) {
          // Presence fetch can fail briefly during reconnect; ignore
        }
      }

      // Listen for enter/leave/update events for any member; refresh the list.
      const listener = (msg) => {
        if (
          msg.data?.key === presenceKey ||
          msg.action === 'leave' ||
          msg.action === 'absent'
        ) {
          refreshEditors()
        }
      }
      await channel.presence.subscribe(listener)
      // Capture the listener reference so the cleanup can unsubscribe —
      // without this the presence listener stays bound across unmounts
      // and every remount stacks another one, slowly leaking.
      subscribedListener = listener

      if (actAsEditor) {
        await channel.presence.enter({
          key: presenceKey,
          name: currentUser, // <- include our display name
        })
      }

      await refreshEditors()
    })()

    return () => {
      cancelled = true
      ;(async () => {
        if (!channel) return
        try {
          // Unsubscribe the presence listener — without this we leak
          // a listener per remount.
          if (subscribedListener) {
            try { channel.presence.unsubscribe(subscribedListener) } catch {}
          }
          if (actAsEditor && presenceKey) {
            await channel.presence.leave({ key: presenceKey, name: currentUser })
          }
        } catch {}
      })()
    }
  }, [pillarName, goalIndex, actAsEditor, currentUser])

  return editors
}
